import { useMemo, useState } from 'react';

import { Spinner, Tabs, TabsList, TabsTrigger } from 'design-system';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import { ChartProvider } from '@/features/charts/context/ChartContext';
import type { ChartType } from '@/features/charts/types/highcharts';
import { useExportChartButton } from '@/features/factsheet/state/ExportChartButtonContext';
import type { FactsheetDetails, FactsheetTab } from '@/features/factsheet/types';
import useIsPermissionsLoaded from '@/shared/hooks/useIsPermissionsLoaded';
import { LightModeProvider } from '@/shared/lib/LightModeContext';
import type { TemplateChartSeries } from '@/shared/types/charts';

import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import AuthenticatedFwdCurveChart from './AuthenticatedFwdCurveChart';
import AuthenticatedSpotChart from './AuthenticatedSpotChart';
import BlurBackdrop from './BlurBackdrop';
import ChartTabContent from './ChartTabContent';
import { createChartSeries } from './createChartSeries';
import UnauthenticatedChart from './UnauthenticatedChart';

const GRAPH_TYPE_MAP: Record<string, ChartType> = { Spot: 'spot', Forward: 'forwardSimple' };

interface ChartTabProps {
  factsheet: FactsheetDetails;
}
const ChartTab = ({ factsheet }: ChartTabProps) => {
  const spotAlias = factsheet.indexDetails['spotAlias'] as string | undefined;
  const isPermissionsLoaded = useIsPermissionsLoaded();
  // Default to 'Spot' when a spot chart is available (matching pre-migration UX),
  // otherwise fall back to 'Forward' so factsheets with only a forward curve
  // (spotSymbols empty, fwdCurveSymbols non-empty) don't get stuck on a tab that
  // doesn't exist in contentTabs.
  const [activeTabName, setActiveTabName] = useState<string>(() =>
    factsheet.spotSymbols.length > 0 ? 'Spot' : 'Forward'
  );
  const { hasPermission } = usePermission();
  const renderExportButton = useExportChartButton();
  const isCompact = useIsFactsheetCompact();

  const isLoading = !isPermissionsLoaded;
  const isAuthorized = isPermissionsLoaded && hasPermission(Permission.ViewIndexInformationChart);

  const templateSpotSeries = useMemo((): TemplateChartSeries[] => {
    if (!isAuthorized || factsheet.spotSymbols.length === 0) return [];
    const symbol = factsheet.spotSymbols[0];
    return [createChartSeries({ factsheet, symbol, spotAlias, includePeriod: true })];
  }, [factsheet, isAuthorized, spotAlias]);

  const templateForwardSeries = useMemo((): TemplateChartSeries[] => {
    if (!isAuthorized || factsheet.fwdCurveSymbols.length === 0) return [];
    const symbol = factsheet.fwdCurveSymbols[0];
    return [createChartSeries({ factsheet, symbol, spotAlias, includePeriod: false })];
  }, [factsheet, isAuthorized, spotAlias]);

  const contentTabs = useMemo((): FactsheetTab[] => {
    const tabs: FactsheetTab[] = [];
    if (factsheet.spotSymbols.length > 0) {
      tabs.push({
        position: 0,
        name: 'Spot',
        component: <AuthenticatedSpotChart templateChartSeries={templateSpotSeries} />
      });
    }
    if (factsheet.fwdCurveSymbols.length > 0) {
      tabs.push({
        position: 1,
        name: 'Forward',
        component: <AuthenticatedFwdCurveChart templateChartSeries={templateForwardSeries} />
      });
    }
    return tabs;
  }, [
    factsheet.spotSymbols.length,
    factsheet.fwdCurveSymbols.length,
    templateSpotSeries,
    templateForwardSeries
  ]);

  if (isLoading) {
    return <Spinner />;
  }

  const activeGraphType = GRAPH_TYPE_MAP[activeTabName] ?? 'spot';

  return (
    <>
      <div className='relative'>
        <BlurBackdrop />
        {!isAuthorized && (
          <ChartProvider>
            <UnauthenticatedChart />
          </ChartProvider>
        )}
      </div>

      {isAuthorized && (
        <ChartProvider>
          <LightModeProvider>
            <Tabs
              value={activeTabName}
              onValueChange={setActiveTabName}
            >
              <div className='flex flex-1 justify-between'>
                {contentTabs.length > 1 && (
                  <TabsList>
                    {contentTabs.map(({ name }) => (
                      <TabsTrigger
                        key={name}
                        value={name}
                      >
                        {name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                )}
                <div className='ml-auto flex items-center'>
                  {renderExportButton?.(
                    activeGraphType,
                    isCompact ? { height: 36 } : undefined,
                    isCompact ? 14 : undefined
                  )}
                </div>
              </div>

              <ChartTabContent
                contentTabs={contentTabs}
                factsheet={factsheet}
              />
            </Tabs>
          </LightModeProvider>
        </ChartProvider>
      )}
    </>
  );
};

export default ChartTab;
